/* eslint-disable compat/compat */
/* eslint-disable no-param-reassign */
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';
import { CameraEffect } from './CameraEffect';
import { GaussianBlur } from './webgl';
const MODEL_URL = 'https://files.instructuremedia.com/mediapipe-models/selfie_segmenter.tflite';
const WASM_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
export class BackgroundBlurEffect extends CameraEffect {
  constructor() {
    super('blur');
    this.blur = new GaussianBlur();
  }
  async init() {
    const vision = await FilesetResolver.forVisionTasks(WASM_URL);
    this.imageSegmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URL
      },
      runningMode: 'VIDEO'
    });
    this.maskedSubject = new OffscreenCanvas(1, 1);
    this.maskedSubjectCtx = this.maskedSubject.getContext('2d', {
      willReadFrequently: true
    });
    this.blur.init();
  }
  cleanup() {
    this.blur.cleanup();
    this.imageSegmenter && this.imageSegmenter.close();
    document.body.querySelectorAll('script[src*=mediapipe]').forEach(e => {
      e.remove();
    });
  }
  drawMask(result) {
    const mask = result.confidenceMasks[0].getAsFloat32Array();
    const imageData = this.maskedSubjectCtx.createImageData(this.maskedSubject.width, this.maskedSubject.height);
    for (let i = 0, j = 0; i < mask.length; ++i, j += 4) {
      imageData.data[j + 3] = mask[i] * 255;
    }
    this.maskedSubjectCtx.putImageData(imageData, 0, 0);
  }
  process(ctx, videoFrame) {
    videoFrame.width = videoFrame.displayWidth;
    videoFrame.height = videoFrame.displayHeight;
    this.maskedSubject.width = videoFrame.displayWidth;
    this.maskedSubject.height = videoFrame.displayHeight;

    // segmenter result will be a transparent mask
    this.imageSegmenter.segmentForVideo(videoFrame, performance.now(), this.drawMask.bind(this));

    // the frame will only be drawn where the mask is not transparent
    this.maskedSubjectCtx.save();
    this.maskedSubjectCtx.globalCompositeOperation = 'source-in';
    this.maskedSubjectCtx.drawImage(videoFrame, 0, 0);
    this.maskedSubjectCtx.restore();
    ctx.drawImage(this.maskedSubject, 0, 0);

    // the original video frame will be blurred and drawn behind the masked subject
    this.blur.resize(videoFrame.displayWidth, videoFrame.displayHeight);
    this.blur.draw(videoFrame);
    ctx.globalCompositeOperation = 'destination-over';
    ctx.drawImage(this.blur.gl.canvas, 0, 0);
    return false;
  }
}